import { isGraphqlConfigured, graphqlRequest } from './graphqlClient';

export { isGraphqlConfigured as isBillingApiConfigured };

/**
 * Validates that a URL returned from the backend is a safe HTTPS URL before
 * redirecting. Prevents open-redirect if the API response is ever tampered with.
 */
export function assertSafeRedirectUrl(url: string, context: string): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error(`${context}: received an invalid URL`);
  }
  if (parsed.protocol !== 'https:') {
    throw new Error(`${context}: URL must use HTTPS`);
  }
}

export interface Entitlement {
  isPro: boolean;
  source: string;
  status: string;
  plan: string | null;
  expiresAt: string | null;
  cancelAtPeriodEnd?: boolean;
}

export interface Invoice {
  id: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  status: string;
  periodStart: string | null;
  periodEnd: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  createdAt: string;
}

/** Mirrors backend `CreateCheckoutSessionInput`. */
export interface CreateCheckoutSessionInput {
  successUrl?: string;
  cancelUrl?: string;
  idempotencyKey?: string;
  plan?: 'monthly' | 'yearly';
  /** Auto-applied at checkout when server-allowlisted; otherwise the backend
   *  falls back to Stripe's manual promo-code field (never blocks checkout). */
  promoCode?: string;
  /** Attribution label ('app', a utm_source, 'direct'). Written to Stripe
   *  session + subscription metadata only; never affects price. */
  source?: string;
  /** utm_campaign attribution label; same handling as `source`. */
  utmCampaign?: string;
}

export interface CheckoutSession {
  id: string;
  url: string;
  /** True only when the requested promo was attached to the Stripe session.
   *  False with no promo, or when the backend fell back to Stripe's manual
   *  promo-code field — the user must then type the code themselves. */
  promoApplied: boolean;
}

export const createCheckoutSession = async (input?: CreateCheckoutSessionInput): Promise<CheckoutSession> => {
  const data = await graphqlRequest<{ createCheckoutSession: CheckoutSession }>(
    `mutation CreateCheckoutSession($input: CreateCheckoutSessionInput) {
      createCheckoutSession(input: $input) { id url promoApplied }
    }`,
    { input: input || {} },
  );
  return data.createCheckoutSession;
};

export const createBillingPortalSession = async (input?: { returnUrl?: string }) => {
  const data = await graphqlRequest<{ createBillingPortalSession: { url: string } }>(
    `mutation CreateBillingPortalSession($input: CreateBillingPortalSessionInput) {
      createBillingPortalSession(input: $input) { url }
    }`,
    { input: input || {} },
  );
  return data.createBillingPortalSession;
};

export const getEntitlement = async (): Promise<Entitlement> => {
  const data = await graphqlRequest<{ entitlement: Entitlement }>(
    `query Entitlement {
      entitlement { isPro source status plan expiresAt cancelAtPeriodEnd }
    }`,
  );
  return data.entitlement;
};

export const cancelSubscription = async (): Promise<{ cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null }> => {
  const data = await graphqlRequest<{ cancelSubscription: { cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null } }>(
    `mutation CancelSubscription {
      cancelSubscription { cancelAtPeriodEnd currentPeriodEnd }
    }`,
  );
  return data.cancelSubscription;
};

export const getInvoices = async (): Promise<Invoice[]> => {
  const data = await graphqlRequest<{ invoices: Invoice[] }>(
    `query Invoices {
      invoices { id amountDue amountPaid currency status periodStart periodEnd hostedInvoiceUrl invoicePdf createdAt }
    }`,
  );
  return data.invoices;
};

export const deleteAccount = async (): Promise<boolean> => {
  const data = await graphqlRequest<{ deleteAccount: boolean }>(
    `mutation DeleteAccount {
      deleteAccount
    }`,
  );
  return data.deleteAccount;
};

const billingApi = { isBillingApiConfigured: isGraphqlConfigured, createCheckoutSession, createBillingPortalSession, getEntitlement, getInvoices, cancelSubscription, deleteAccount };
export default billingApi;
